/**
 * Chat Store
 *
 * Manages chat state using Zustand.
 * Provides chat session management, message handling, and Claude CLI streaming state.
 *
 * Usage:
 *   import { useChatStore } from '@/stores/chatStore';
 *   const { sessions, activeSession, sendMessage, createSession } = useChatStore();
 */

import { create } from 'zustand';
import {
  api,
  ChatSession,
  ChatMessage,
  ChatInteraction,
  ChatSessionDetail,
  ApiRequestError,
} from '@/services/api';
import type { ChatServerEvent, ChatContentBlock } from '@/hooks/useWebSocket';
import { usePinsStore } from '@/stores/pinsStore';
import {
  createChatStreamRecord,
  getSessionStream,
  isRunningStream,
  removeStream,
  type ChatStreamRecord,
  type ChatStreamProvider,
} from './chatStreamState';

// ============================================
// Types
// ============================================

/**
 * Streaming state for Claude CLI interaction
 */
export type StreamingState = 'idle' | 'connecting' | 'streaming' | 'cancelling';

/**
 * A message in the chat (includes pending messages not yet saved to server)
 */
export interface LocalMessage {
  id: string | number;
  role: 'user' | 'assistant' | 'system';
  content: string;
  images: string[] | null;
  timestamp: string;
  interaction?: ChatInteraction | null;
  isInteractionResponse?: boolean;
  grounding?: ChatMessage['grounding'];
  authorUserId?: number | null;
  authorUsername?: string | null;
  // Local-only fields
  isPending?: boolean; // True while waiting for server confirmation
  isStreaming?: boolean; // True while assistant is streaming response
}

/**
 * Extended chat session with loaded messages
 */
export interface ChatSessionWithMessages extends ChatSession {
  messages: LocalMessage[];
  isLoaded: boolean;
}

export interface ProviderConnectionRequirement {
  provider: ChatStreamProvider;
  sessionId: string;
}

export interface ChatState {
  // Session state
  sessions: ChatSession[];
  sessionsLoading: boolean;
  sessionsError: string | null;

  // Active session with messages
  activeSessionId: string | null;
  activeSession: ChatSessionWithMessages | null;
  sessionDetailsById: Record<string, ChatSessionWithMessages>;
  sessionLoading: boolean;
  sessionError: string | null;

  // Streaming state for Claude CLI
  streamingState: StreamingState;
  streamingText: string;
  streamingContent: ChatContentBlock[];
  streamId: string | null;
  streamForSession: string | null; // Session ID that owns current stream
  streamError: string | null;
  providerConnectionRequired: ProviderConnectionRequirement | null;
  activeStreamsBySession: Record<string, string>;
  streamsById: Record<string, ChatStreamRecord>;

  // Activity tracking (from server heartbeat)
  streamStartTime: number | null;     // When stream started (ms since epoch)
  streamElapsed: number;              // Elapsed time from last activity event (ms)
  activeToolName: string | null;      // Currently executing tool name
  activeToolId: string | null;        // Currently executing tool ID
  lastActivityTime: number | null;    // When we last received any event (for client-side staleness detection)

  // Cross-component composer pre-fill (e.g. Skills Catalog -> chat composer)
  pendingComposerText: string | null;

  // Cross-component composer insert-at-cursor (e.g. file-reference picker -> chat
  // composer). Distinct from pendingComposerText: that REPLACES the draft, this
  // splices text in at the caret (ADR-0009).
  pendingComposerInsert: string | null;
  pendingComposerImages: string[];

  // Skills Catalog open state (toggled from composer Sparkles button + Cmd/Ctrl+K)
  isCatalogOpen: boolean;

  // Actions - Sessions
  loadSessions: () => Promise<void>;
  /** `timezone` dates the default session name in the user's timezone. */
  createSession: (name?: string, provider?: 'claude' | 'codex', timezone?: string) => Promise<ChatSession | null>;
  /** "Continue a copy": copy a team chat into a new chat that the user owns, then open it. */
  forkSession: (id: string) => Promise<ChatSession | null>;
  deleteSession: (id: string) => Promise<boolean>;
  renameSession: (id: string, name: string) => Promise<boolean>;
  selectSession: (id: string | null) => Promise<void>;
  /** Reload a session's saved messages, for messages the server added outside a stream. */
  refreshSession: (id: string) => Promise<void>;
  updateSessionLlmConfig: (
    id: string,
    config: { provider?: 'claude' | 'codex'; model?: string | null; reasoningEffort?: string | null }
  ) => Promise<boolean>;
  updateClaudeSessionId: (sessionId: string, claudeSessionId: string) => Promise<void>;

  // Actions - Messages
  loadMessages: (sessionId: string) => Promise<void>;
  addUserMessage: (content: string, images?: string[]) => Promise<LocalMessage | null>;
  addAssistantMessage: (content: string) => Promise<LocalMessage | null>;
  addAssistantMessageToSession: (sessionId: string, content: string, streamId?: string) => Promise<LocalMessage | null>;

  // Actions - Streaming
  startStreaming: (streamId: string, opts?: { forSessionId?: string; silent?: boolean; provider?: ChatStreamProvider }) => void;
  handleStreamEvent: (event: ChatServerEvent) => void;
  cancelStreaming: (streamId?: string) => void;
  clearStreamError: () => void;
  clearProviderConnectionRequired: () => void;

  // Actions - Composer pre-fill
  setPendingComposerText: (text: string | null) => void;
  setPendingComposerInsert: (text: string | null) => void;
  setPendingComposerImages: (images: string[]) => void;

  // Actions - Skills Catalog
  setIsCatalogOpen: (open: boolean) => void;

  // Utilities
  getStreamForSession: (sessionId: string | null | undefined) => ChatStreamRecord | null;
  isSessionStreaming: (sessionId: string | null | undefined) => boolean;
  getSessionById: (id: string) => ChatSession | undefined;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Helpers
// ============================================

/**
 * Generate a unique temporary ID for pending messages
 */
function generateTempId(): string {
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Get current ISO timestamp
 */
function getCurrentTimestamp(): string {
  return new Date().toISOString();
}

/**
 * Convert API ChatMessage to LocalMessage
 */
function toLocalMessage(msg: ChatMessage): LocalMessage {
  return {
    id: msg.id,
    role: msg.role,
    content: msg.content,
    images: msg.images,
    timestamp: msg.timestamp,
    interaction: msg.interaction,
    isInteractionResponse: msg.isInteractionResponse,
    grounding: msg.grounding ?? null,
    authorUserId: msg.authorUserId ?? null,
    authorUsername: msg.authorUsername ?? null,
    isPending: false,
    isStreaming: false,
  };
}

/**
 * Extract text from Claude content blocks
 */
function extractTextFromContent(content: ChatContentBlock[]): string {
  return content
    .filter((block) => block.type === 'text' && block.text)
    .map((block) => block.text)
    .join('');
}

function stripInteractionDirectivePreview(value: string): string {
  const fenceIndex = value.indexOf('```telcoos-interaction');
  return fenceIndex >= 0 ? value.slice(0, fenceIndex).trimEnd() : value;
}

function insertBeforeStreamingMessage(
  messages: LocalMessage[],
  message: LocalMessage,
  streamId: string
): LocalMessage[] {
  if (messages.some((existing) => existing.id === message.id)) return messages;
  const streamingMessageId = getStreamingMessageId(streamId);
  const index = messages.findIndex((existing) => existing.id === streamingMessageId);
  if (index < 0) return [...messages, message];
  return [...messages.slice(0, index), message, ...messages.slice(index)];
}

function isClaudeResumeSessionId(value: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value);
}

function getStreamingMessageId(streamId: string): string {
  return `streaming-${streamId}`;
}

function withoutStreamingMessage(messages: LocalMessage[], streamId: string): LocalMessage[] {
  const streamingMessageId = getStreamingMessageId(streamId);
  return messages.filter((m) => m.id !== streamingMessageId && m.id !== 'streaming');
}

function withoutStreamingMessages(messages: LocalMessage[]): LocalMessage[] {
  return messages.filter((m) => !m.isStreaming);
}

function appendMessageIfMissing(messages: LocalMessage[], message: LocalMessage): LocalMessage[] {
  if (messages.some((existing) => existing.id === message.id)) {
    return messages;
  }

  return [...withoutStreamingMessages(messages), message];
}

function hasMessage(messages: LocalMessage[], message: LocalMessage): boolean {
  return messages.some((existing) => existing.id === message.id);
}

function replaceMessageById(
  messages: LocalMessage[],
  targetId: string | number,
  replacement: LocalMessage
): LocalMessage[] {
  return messages.map((message) => (message.id === targetId ? replacement : message));
}

function sessionDetailToLocalSession(sessionDetail: ChatSessionDetail): ChatSessionWithMessages {
  return {
    id: sessionDetail.id,
    name: sessionDetail.name,
    provider: sessionDetail.provider,
    model: sessionDetail.model,
    reasoningEffort: sessionDetail.reasoningEffort,
    claudeSessionId: sessionDetail.claudeSessionId,
    messageCount: sessionDetail.messages.length,
    createdAt: sessionDetail.createdAt,
    updatedAt: sessionDetail.updatedAt,
    ownerUserId: sessionDetail.ownerUserId,
    ownerUsername: sessionDetail.ownerUsername,
    ownerDeleted: sessionDetail.ownerDeleted,
    forkedFromSessionId: sessionDetail.forkedFromSessionId,
    canPost: sessionDetail.canPost,
    canDelete: sessionDetail.canDelete,
    messages: sessionDetail.messages.map(toLocalMessage),
    isLoaded: true,
  };
}

function preserveLocalPendingMessages(
  serverSession: ChatSessionWithMessages,
  localSession: ChatSessionWithMessages | null | undefined
): ChatSessionWithMessages {
  if (!localSession || localSession.id !== serverSession.id) {
    return serverSession;
  }

  const serverMessageIds = new Set(serverSession.messages.map((message) => message.id));
  const pendingMessages = withoutStreamingMessages(localSession.messages).filter(
    (message) => message.isPending && !serverMessageIds.has(message.id)
  );

  if (pendingMessages.length === 0) {
    return serverSession;
  }

  return {
    ...serverSession,
    messages: [...serverSession.messages, ...pendingMessages],
    messageCount: serverSession.messageCount + pendingMessages.length,
  };
}

function withSessionStreamPlaceholder(
  session: ChatSessionWithMessages,
  state: Pick<ChatState, 'activeStreamsBySession' | 'streamsById'>,
  sessionId: string
): ChatSessionWithMessages {
  const baseMessages = withoutStreamingMessages(session.messages);
  const sessionStream = getSessionStream(
    {
      activeStreamsBySession: state.activeStreamsBySession,
      streamsById: state.streamsById,
    },
    sessionId
  );

  if (!sessionStream || !isRunningStream(sessionStream)) {
    return {
      ...session,
      messages: baseMessages,
    };
  }

  return {
    ...session,
    messages: [
      ...baseMessages,
      {
        id: getStreamingMessageId(sessionStream.streamId),
        role: 'assistant',
        content: sessionStream.text,
        images: null,
        timestamp: getCurrentTimestamp(),
        isStreaming: true,
      },
    ],
  };
}

function applyActiveSessionStreamFields(
  state: Pick<
    ChatState,
    | 'activeSessionId'
    | 'activeStreamsBySession'
    | 'streamsById'
  >
): Pick<
  ChatState,
  | 'streamingState'
  | 'streamingText'
  | 'streamingContent'
  | 'streamId'
  | 'streamForSession'
  | 'streamError'
  | 'streamStartTime'
  | 'streamElapsed'
  | 'activeToolName'
  | 'activeToolId'
  | 'lastActivityTime'
> {
  const stream = getSessionStream(state, state.activeSessionId);

  if (!stream) {
    return {
      streamingState: 'idle',
      streamingText: '',
      streamingContent: [],
      streamId: null,
      streamForSession: null,
      streamError: null,
      streamStartTime: null,
      streamElapsed: 0,
      activeToolName: null,
      activeToolId: null,
      lastActivityTime: null,
    };
  }

  return {
    streamingState: stream.state,
    streamingText: stream.text,
    streamingContent: stream.content,
    streamId: stream.streamId,
    streamForSession: stream.sessionId,
    streamError: stream.error,
    streamStartTime: stream.startTime,
    streamElapsed: stream.elapsed,
    activeToolName: stream.activeToolName,
    activeToolId: stream.activeToolId,
    lastActivityTime: stream.lastActivityTime,
  };
}

// ============================================
// Initial State
// ============================================

const initialState = {
  // Sessions
  sessions: [] as ChatSession[],
  sessionsLoading: false,
  sessionsError: null as string | null,

  // Active session
  activeSessionId: null as string | null,
  activeSession: null as ChatSessionWithMessages | null,
  sessionDetailsById: {} as Record<string, ChatSessionWithMessages>,
  sessionLoading: false,
  sessionError: null as string | null,

  // Streaming
  streamingState: 'idle' as StreamingState,
  streamingText: '',
  streamingContent: [] as ChatContentBlock[],
  streamId: null as string | null,
  streamForSession: null as string | null,
  streamError: null as string | null,
  providerConnectionRequired: null as ProviderConnectionRequirement | null,
  activeStreamsBySession: {} as Record<string, string>,
  streamsById: {} as Record<string, ChatStreamRecord>,

  // Activity tracking
  streamStartTime: null as number | null,
  streamElapsed: 0,
  activeToolName: null as string | null,
  activeToolId: null as string | null,
  lastActivityTime: null as number | null,

  // Composer pre-fill
  pendingComposerText: null as string | null,
  pendingComposerInsert: null as string | null,
  pendingComposerImages: [] as string[],

  // Skills Catalog
  isCatalogOpen: false,
};

// ============================================
// Store
// ============================================

export const useChatStore = create<ChatState>((set, get) => ({
  ...initialState,

  // ----------------------------------------
  // Session Actions
  // ----------------------------------------

  /**
   * Load all chat sessions from the server
   */
  loadSessions: async () => {
    const state = get();
    if (state.sessionsLoading) return;

    set({ sessionsLoading: true, sessionsError: null });

    try {
      const response = await api.chat.getSessions();

      set((state) => {
        const liveSessionIds = new Set(response.sessions.map((session) => session.id));
        const sessionDetailsById = Object.fromEntries(
          Object.entries(state.sessionDetailsById).filter(([id]) => liveSessionIds.has(id))
        );

        return {
          sessions: response.sessions,
          sessionsLoading: false,
          sessionDetailsById,
        };
      });

      // Auto-select the user's most recent chat if none is active.
      // Sessions are sorted by updatedAt descending, so the first is most recent.
      const currentState = get();
      const latestOwnSession = response.sessions.find((session) => session.canPost);
      if (!currentState.activeSessionId && latestOwnSession) {
        await get().selectSession(latestOwnSession.id);
      }
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load chat sessions';

      set({
        sessionsLoading: false,
        sessionsError: message,
      });
    }
  },

  /**
   * Create a new chat session
   */
  createSession: async (name?: string, provider: 'claude' | 'codex' = 'claude', timezone?: string) => {
    try {
      const session = await api.chat.createSession(name, provider, timezone);

      // Add to sessions list
      set((state) => ({
        sessions: [session, ...state.sessions],
        sessionDetailsById: {
          ...state.sessionDetailsById,
          [session.id]: {
            ...session,
            messages: [],
            isLoaded: true,
          },
        },
      }));

      return session;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to create session';

      set({ sessionsError: message });
      return null;
    }
  },

  forkSession: async (id: string) => {
    try {
      const session = await api.chat.forkSession(id);
      set((state) => ({ sessions: [session, ...state.sessions] }));
      await get().selectSession(session.id);
      return session;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to copy session';

      set({ sessionsError: message });
      return null;
    }
  },

  /**
   * Delete a chat session
   */
  deleteSession: async (id: string) => {
    try {
      await api.chat.deleteSession(id);

      const state = get();

      // Remove from sessions list
      set((prevState) => ({
        sessions: prevState.sessions.filter((s) => s.id !== id),
        sessionDetailsById: Object.fromEntries(
          Object.entries(prevState.sessionDetailsById).filter(([sessionId]) => sessionId !== id)
        ),
        // Clear active session if it was deleted
        activeSessionId: prevState.activeSessionId === id ? null : prevState.activeSessionId,
        activeSession: prevState.activeSessionId === id ? null : prevState.activeSession,
      }));

      usePinsStore.setState((p) => ({
        pins: p.pins.filter((pin) => !(pin.kind === 'chat' && pin.entityId === id)),
      }));

      // If we deleted the active session and there are other sessions, select the first one
      if (state.activeSessionId === id && state.sessions.length > 1) {
        const remainingSessions = state.sessions.filter((s) => s.id !== id);
        if (remainingSessions.length > 0) {
          // Don't auto-select, let user choose
        }
      }

      return true;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to delete session';

      set({ sessionsError: message });
      return false;
    }
  },

  /**
   * Rename a chat session
   */
  renameSession: async (id: string, name: string) => {
    try {
      const updated = await api.chat.updateSession(id, { name });

      set((state) => ({
        sessions: state.sessions.map((s) =>
          s.id === id ? { ...s, name: updated.name } : s
        ),
        activeSession:
          state.activeSession?.id === id
            ? { ...state.activeSession, name: updated.name }
            : state.activeSession,
        sessionDetailsById: {
          ...state.sessionDetailsById,
          ...(state.sessionDetailsById[id]
            ? { [id]: { ...state.sessionDetailsById[id], name: updated.name } }
            : {}),
        },
      }));

      usePinsStore.setState((p) => ({
        pins: p.pins.map((pin) =>
          pin.kind === 'chat' && pin.entityId === id ? { ...pin, label: updated.name } : pin
        ),
      }));

      return true;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to rename session';

      set({ sessionsError: message });
      return false;
    }
  },

  /**
   * Update provider/model/thinking for an empty chat.
   */
  updateSessionLlmConfig: async (id, config) => {
    try {
      const updated = await api.chat.updateSession(id, config);

      set((state) => ({
        sessions: state.sessions.map((s) =>
          s.id === id
            ? {
                ...s,
                provider: updated.provider,
                model: updated.model,
                reasoningEffort: updated.reasoningEffort,
                updatedAt: updated.updatedAt,
              }
            : s
        ),
        activeSession:
          state.activeSession?.id === id
            ? {
                ...state.activeSession,
                provider: updated.provider,
                model: updated.model,
                reasoningEffort: updated.reasoningEffort,
                updatedAt: updated.updatedAt,
              }
            : state.activeSession,
        sessionDetailsById: {
          ...state.sessionDetailsById,
          ...(state.sessionDetailsById[id]
            ? {
                [id]: {
                  ...state.sessionDetailsById[id],
                  provider: updated.provider,
                  model: updated.model,
                  reasoningEffort: updated.reasoningEffort,
                  updatedAt: updated.updatedAt,
                },
              }
            : {}),
        },
      }));

      return true;
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to update LLM settings';

      set({ sessionError: message });
      return false;
    }
  },

  /**
   * Select a session and load its messages
   */
  selectSession: async (id: string | null) => {
    if (id === null) {
      set({
        activeSessionId: null,
        activeSession: null,
        sessionError: null,
        ...applyActiveSessionStreamFields({
          activeSessionId: null,
          activeStreamsBySession: get().activeStreamsBySession,
          streamsById: get().streamsById,
        }),
      });
      return;
    }

    const state = get();

    // If already selected and loaded, just return
    if (state.activeSessionId === id && state.activeSession?.isLoaded) {
      return;
    }

    const cachedSession = state.sessionDetailsById[id];
    if (cachedSession) {
      set({
        activeSessionId: id,
        activeSession: withSessionStreamPlaceholder(cachedSession, state, id),
        sessionLoading: false,
        sessionError: null,
        ...applyActiveSessionStreamFields({
          activeSessionId: id,
          activeStreamsBySession: state.activeStreamsBySession,
          streamsById: state.streamsById,
        }),
      });
    } else {
      set({
        activeSessionId: id,
        sessionLoading: true,
        sessionError: null,
      });
    }

    try {
      const sessionDetail: ChatSessionDetail = await api.chat.getSession(id);
      const sessionWithMessages = sessionDetailToLocalSession(sessionDetail);

      set((prevState) => {
        const reconciledSession = preserveLocalPendingMessages(
          sessionWithMessages,
          prevState.activeSessionId === id
            ? prevState.activeSession
            : prevState.sessionDetailsById[id]
        );

        return {
          sessionDetailsById: {
            ...prevState.sessionDetailsById,
            [id]: reconciledSession,
          },
          ...(prevState.activeSessionId === id
            ? {
                activeSession: withSessionStreamPlaceholder(reconciledSession, prevState, id),
                sessionLoading: false,
                ...applyActiveSessionStreamFields({
                  activeSessionId: id,
                  activeStreamsBySession: prevState.activeStreamsBySession,
                  streamsById: prevState.streamsById,
                }),
              }
            : {}),
        };
      });
    } catch (err) {
      const message =
        err instanceof ApiRequestError
          ? err.message
          : err instanceof Error
            ? err.message
            : 'Failed to load session';

      set({
        sessionLoading: false,
        sessionError: cachedSession ? null : message,
      });
    }
  },

  refreshSession: async (id: string) => {
    const sessionDetail: ChatSessionDetail = await api.chat.getSession(id);
    const sessionWithMessages = sessionDetailToLocalSession(sessionDetail);
    set((prevState) => {
      const reconciledSession = preserveLocalPendingMessages(
        sessionWithMessages,
        prevState.activeSessionId === id
          ? prevState.activeSession
          : prevState.sessionDetailsById[id]
      );
      return {
        sessionDetailsById: {
          ...prevState.sessionDetailsById,
          [id]: reconciledSession,
        },
        ...(prevState.activeSessionId === id
          ? { activeSession: withSessionStreamPlaceholder(reconciledSession, prevState, id) }
          : {}),
      };
    });
  },

  /**
   * Update the Claude session ID for --resume functionality
   */
  updateClaudeSessionId: async (sessionId: string, claudeSessionId: string) => {
    try {
      await api.chat.updateSession(sessionId, { claudeSessionId });

      set((state) => ({
        sessions: state.sessions.map((s) =>
          s.id === sessionId ? { ...s, claudeSessionId } : s
        ),
        activeSession:
          state.activeSession?.id === sessionId
            ? { ...state.activeSession, claudeSessionId }
            : state.activeSession,
        sessionDetailsById: {
          ...state.sessionDetailsById,
          ...(state.sessionDetailsById[sessionId]
            ? { [sessionId]: { ...state.sessionDetailsById[sessionId], claudeSessionId } }
            : {}),
        },
      }));
    } catch (err) {
      console.error('Failed to update Claude session ID:', err);
      // Non-critical error, don't set error state
    }
  },

  // ----------------------------------------
  // Message Actions
  // ----------------------------------------

  /**
   * Load messages for a session (if not already loaded)
   */
  loadMessages: async (sessionId: string) => {
    const state = get();

    // If this session is already active and loaded, skip
    if (state.activeSession?.id === sessionId && state.activeSession.isLoaded) {
      return;
    }

    await get().selectSession(sessionId);
  },

  /**
   * Add a user message to the active session
   * Returns the local message immediately, saves to server in background
   */
  addUserMessage: async (content: string, images?: string[]) => {
    const state = get();

    if (!state.activeSession) {
      console.error('No active session to add message to');
      return null;
    }

    const sessionId = state.activeSession.id;
    const tempId = generateTempId();

    // Create local message immediately
    const localMessage: LocalMessage = {
      id: tempId,
      role: 'user',
      content,
      images: images || null,
      timestamp: getCurrentTimestamp(),
      isPending: true,
    };

    // Add to messages optimistically
    set((prevState) => {
      const cachedSession = prevState.sessionDetailsById[sessionId];
      const currentCount = cachedSession?.messageCount ?? prevState.activeSession?.messageCount ?? 0;
      const newCount = currentCount + 1;

      return {
        activeSession:
          prevState.activeSession?.id === sessionId
            ? {
                ...prevState.activeSession,
                messages: appendMessageIfMissing(prevState.activeSession.messages, localMessage),
                messageCount: newCount,
              }
            : prevState.activeSession,
        sessionDetailsById: cachedSession
          ? {
              ...prevState.sessionDetailsById,
              [sessionId]: {
                ...cachedSession,
                messages: appendMessageIfMissing(cachedSession.messages, localMessage),
                messageCount: newCount,
              },
            }
          : prevState.sessionDetailsById,
        // Also update the sessions array for sidebar
        sessions: prevState.sessions.map((s) =>
          s.id === sessionId ? { ...s, messageCount: newCount } : s
        ),
      };
    });

    // Save to server
    try {
      const savedMessage = await api.chat.addMessage(sessionId, 'user', content, images);

      // Update with server ID
      set((prevState) => {
        const savedLocalMessage = { ...toLocalMessage(savedMessage), isPending: false };
        const cachedSession = prevState.sessionDetailsById[sessionId];

        return {
          activeSession:
            prevState.activeSession?.id === sessionId
              ? {
                  ...prevState.activeSession,
                  messages: replaceMessageById(prevState.activeSession.messages, tempId, savedLocalMessage),
                }
              : prevState.activeSession,
          sessionDetailsById: cachedSession
            ? {
                ...prevState.sessionDetailsById,
                [sessionId]: {
                  ...cachedSession,
                  messages: replaceMessageById(cachedSession.messages, tempId, savedLocalMessage),
                },
              }
            : prevState.sessionDetailsById,
        };
      });

      return { ...localMessage, id: savedMessage.id, isPending: false };
    } catch (err) {
      console.error('Failed to save user message:', err);
      // Keep the local message but mark as error? For now, just log
      return localMessage;
    }
  },

  /**
   * Add an assistant message to the active session
   * Called after streaming completes
   */
  addAssistantMessage: async (content: string) => {
    const state = get();

    if (!state.activeSession) {
      console.error('No active session to add message to');
      return null;
    }

    const sessionId = state.activeSession.id;

    try {
      const savedMessage = await api.chat.addMessage(sessionId, 'assistant', content);
      const localMessage = toLocalMessage(savedMessage);

      // Add to messages
      set((prevState) => {
        const cachedSession = prevState.sessionDetailsById[sessionId];
        const currentCount = cachedSession?.messageCount ?? prevState.activeSession?.messageCount ?? 0;
        const newCount = currentCount + 1;

        return {
          activeSession:
            prevState.activeSession?.id === sessionId
              ? {
                  ...prevState.activeSession,
                  messages: appendMessageIfMissing(prevState.activeSession.messages, localMessage),
                  messageCount: newCount,
                }
              : prevState.activeSession,
          sessionDetailsById: cachedSession
            ? {
                ...prevState.sessionDetailsById,
                [sessionId]: {
                  ...cachedSession,
                  messages: appendMessageIfMissing(cachedSession.messages, localMessage),
                  messageCount: newCount,
                },
              }
            : prevState.sessionDetailsById,
          // Also update the sessions array for sidebar
          sessions: prevState.sessions.map((s) =>
            s.id === sessionId ? { ...s, messageCount: newCount } : s
          ),
        };
      });

      return localMessage;
    } catch (err) {
      console.error('Failed to save assistant message:', err);

      // Still add locally even if save fails
      const localMessage: LocalMessage = {
        id: generateTempId(),
        role: 'assistant',
        content,
        images: null,
        timestamp: getCurrentTimestamp(),
        isPending: true,
      };

      set((prevState) => {
        const cachedSession = prevState.sessionDetailsById[sessionId];
        const currentCount = cachedSession?.messageCount ?? prevState.activeSession?.messageCount ?? 0;
        const newCount = currentCount + 1;

        return {
          activeSession:
            prevState.activeSession?.id === sessionId
              ? {
                  ...prevState.activeSession,
                  messages: appendMessageIfMissing(prevState.activeSession.messages, localMessage),
                  messageCount: newCount,
                }
              : prevState.activeSession,
          sessionDetailsById: cachedSession
            ? {
                ...prevState.sessionDetailsById,
                [sessionId]: {
                  ...cachedSession,
                  messages: appendMessageIfMissing(cachedSession.messages, localMessage),
                  messageCount: newCount,
                },
              }
            : prevState.sessionDetailsById,
          // Also update the sessions array for sidebar
          sessions: prevState.sessions.map((s) =>
            s.id === sessionId ? { ...s, messageCount: newCount } : s
          ),
        };
      });

      return localMessage;
    }
  },

  /**
   * Add an assistant message to a specific session
   * Used when streaming completes to save to the original session even if user switched
   */
  addAssistantMessageToSession: async (sessionId: string, content: string, streamId?: string) => {
    try {
      // Pass streamId so this POST dedupes against the server's own persist of
      // the same turn (ADR-0008): both carry the same key, server returns the
      // already-stored row on conflict instead of inserting a duplicate.
      const savedMessage = await api.chat.addMessage(sessionId, 'assistant', content, undefined, streamId);
      const localMessage = toLocalMessage(savedMessage);

      // Update UI - always update sessions array, only update activeSession if it matches
      set((prevState) => {
        const isActiveSession = prevState.activeSession?.id === sessionId;
        const cachedSession = prevState.sessionDetailsById[sessionId];
        const alreadyHasMessage = cachedSession
          ? hasMessage(cachedSession.messages, localMessage)
          : Boolean(isActiveSession && hasMessage(prevState.activeSession!.messages, localMessage));
        const currentCount = cachedSession?.messageCount ??
          (isActiveSession
            ? prevState.activeSession!.messageCount
            : (prevState.sessions.find(s => s.id === sessionId)?.messageCount || 0));
        const newCount = alreadyHasMessage ? currentCount : currentCount + 1;

        return {
          // Update activeSession if it's the target session
          activeSession: isActiveSession
            ? {
                ...prevState.activeSession!,
                messages: appendMessageIfMissing(prevState.activeSession!.messages, localMessage),
                messageCount: newCount,
              }
            : prevState.activeSession,
          sessionDetailsById: cachedSession
            ? {
                ...prevState.sessionDetailsById,
                [sessionId]: {
                  ...cachedSession,
                  messages: appendMessageIfMissing(cachedSession.messages, localMessage),
                  messageCount: newCount,
                },
              }
            : prevState.sessionDetailsById,
          // Always update the sessions array for sidebar
          sessions: prevState.sessions.map((s) =>
            s.id === sessionId ? { ...s, messageCount: newCount } : s
          ),
        };
      });
      return localMessage;
    } catch (err) {
      console.error('Failed to save assistant message to session:', sessionId, err);
      return null;
    }
  },

  // ----------------------------------------
  // Streaming Actions
  // ----------------------------------------

  /**
   * Mark that streaming has started.
   *
   * `opts.forSessionId` keeps an existing stream bound to its originating
   * session when the user views a different chat; it defaults to the active
   * session. `opts.silent` suppresses a placeholder for a chat that is not on
   * screen.
   */
  startStreaming: (streamId: string, opts) => {
    const forSessionId = opts?.forSessionId ?? get().activeSession?.id ?? null;
    const silent = opts?.silent ?? false;
    const provider = opts?.provider ?? 'claude';

    if (!forSessionId) return;

    set((state) => {
      const existingStreamId = state.activeStreamsBySession[forSessionId];
      const collection = existingStreamId
        ? removeStream(state, existingStreamId)
        : {
            activeStreamsBySession: state.activeStreamsBySession,
            streamsById: state.streamsById,
          };

      const stream = createChatStreamRecord(streamId, forSessionId, provider);
      const activeStreamsBySession = {
        ...collection.activeStreamsBySession,
        [forSessionId]: streamId,
      };
      const streamsById = {
        ...collection.streamsById,
        [streamId]: stream,
      };

      // Only show the placeholder when the stream belongs to the chat on screen.
      const base = {
        activeStreamsBySession,
        streamsById,
        providerConnectionRequired:
          state.providerConnectionRequired?.sessionId === forSessionId
            ? null
            : state.providerConnectionRequired,
        ...applyActiveSessionStreamFields({
          activeSessionId: state.activeSessionId,
          activeStreamsBySession,
          streamsById,
        }),
      };

      if (silent || !state.activeSession || state.activeSession.id !== forSessionId) {
        return base;
      }

      const streamingMessage: LocalMessage = {
        id: getStreamingMessageId(streamId),
        role: 'assistant',
        content: '',
        images: null,
        timestamp: getCurrentTimestamp(),
        isStreaming: true,
      };

      return {
        ...base,
        activeSession: {
          ...state.activeSession,
          messages: [
            ...withoutStreamingMessage(state.activeSession.messages, existingStreamId || streamId),
            streamingMessage,
          ],
        },
      };
    });
  },

  /**
   * Handle a streaming event from Claude WebSocket
   */
  handleStreamEvent: (event: ChatServerEvent) => {
    const state = get();
    const eventStreamId = event.streamId ?? state.streamId;
    const stream = eventStreamId ? state.streamsById[eventStreamId] : null;
    if (!stream && event.type !== 'error' && event.type !== 'provider_connection_required') return;

    const updateStream = (
      updater: (stream: ChatStreamRecord) => ChatStreamRecord,
      activeSessionUpdater?: (prevState: ChatState, stream: ChatStreamRecord) => ChatState['activeSession']
    ) => {
      if (!eventStreamId) return;

      set((prevState) => {
        const currentStream = prevState.streamsById[eventStreamId];
        if (!currentStream) return prevState;

        const updatedStream = updater(currentStream);
        const streamsById = {
          ...prevState.streamsById,
          [eventStreamId]: updatedStream,
        };

        return {
          streamsById,
          activeSession: activeSessionUpdater
            ? activeSessionUpdater(prevState, updatedStream)
            : prevState.activeSession,
          ...applyActiveSessionStreamFields({
            activeSessionId: prevState.activeSessionId,
            activeStreamsBySession: prevState.activeStreamsBySession,
            streamsById,
          }),
        };
      });
    };

    switch (event.type) {
      case 'init':
        // Store the Claude session ID for --resume using the original session
        if (event.sessionId && isClaudeResumeSessionId(event.sessionId) && stream?.sessionId) {
          get().updateClaudeSessionId(stream.sessionId, event.sessionId);
        }
        updateStream((currentStream) => ({
          ...currentStream,
          state: 'streaming',
          startTime: Date.now(),
          lastActivityTime: Date.now(),
        }));
        break;

      case 'message': {
        const text = stripInteractionDirectivePreview(event.text || '');
        const newContent = event.content || [];

        updateStream((currentStream) => {
          const seenToolUseIds = new Set(
            currentStream.content
              .filter(b => b.type === 'tool_use')
              .map(b => b.id)
          );
          const seenToolResultIds = new Set(
            currentStream.content
              .filter(b => b.type === 'tool_result')
              .map(b => b.tool_use_id)
          );

          const newToolBlocks = newContent.filter(b => {
            if (b.type === 'tool_use' && b.id && !seenToolUseIds.has(b.id)) return true;
            if (b.type === 'tool_result' && b.tool_use_id && !seenToolResultIds.has(b.tool_use_id)) return true;
            return false;
          });

          return {
            ...currentStream,
            text,
            content: [...currentStream.content, ...newToolBlocks],
            lastActivityTime: Date.now(),
          };
        }, (prevState, updatedStream) => {
          if (prevState.activeSession?.id !== updatedStream.sessionId) {
            return prevState.activeSession;
          }
          return {
            ...prevState.activeSession,
            messages: prevState.activeSession.messages.map((m) =>
              m.id === getStreamingMessageId(updatedStream.streamId) || m.id === 'streaming'
                ? { ...m, content: text }
                : m
            ),
          };
        });
        break;
      }

      case 'result': {
        // Streaming complete - finalize the message
        if (!stream || !eventStreamId) return;

        const finalText = event.result || stream.text || extractTextFromContent(stream.content) || '';
        const originalSessionId = stream.sessionId;
        const resultStreamId = stream.streamId;

        // Update Claude session ID if provided in result (more reliable than init)
        if (event.sessionId && isClaudeResumeSessionId(event.sessionId)) {
          get().updateClaudeSessionId(originalSessionId, event.sessionId);
        }

        // Remove the streaming placeholder and save the final message
        set((prevState) => {
          const collection = removeStream(prevState, resultStreamId);
          const markInteractionComplete = (messages: LocalMessage[]) =>
            messages.map((message) =>
              message.interaction?.resumeStreamId === resultStreamId
                ? {
                    ...message,
                    interaction: { ...message.interaction, resumeCompleted: true },
                  }
                : message
            );
          const cachedSession = prevState.sessionDetailsById[originalSessionId];
          return {
            ...collection,
            ...applyActiveSessionStreamFields({
              activeSessionId: prevState.activeSessionId,
              ...collection,
            }),
            activeSession:
              prevState.activeSession?.id === originalSessionId
                ? {
                    ...prevState.activeSession,
                    messages: markInteractionComplete(
                      withoutStreamingMessage(prevState.activeSession.messages, resultStreamId)
                    ),
                  }
                : prevState.activeSession,
            sessionDetailsById: cachedSession
              ? {
                  ...prevState.sessionDetailsById,
                  [originalSessionId]: {
                    ...cachedSession,
                    messages: markInteractionComplete(
                      withoutStreamingMessage(cachedSession.messages, resultStreamId)
                    ),
                  },
                }
              : prevState.sessionDetailsById,
          };
        });

        // Save the assistant message to the original session
        if (finalText) {
          get().addAssistantMessageToSession(originalSessionId, finalText, resultStreamId);
        }
        break;
      }

      case 'interaction': {
        if (!event.interaction || !eventStreamId) return;
        const interaction = event.interaction;
        if (event.assistantMessage) {
          const assistantMessage = {
            ...toLocalMessage(event.assistantMessage),
            interaction,
          };
          set((prevState) => {
            const collection = removeStream(prevState, eventStreamId);
            const cachedSession = prevState.sessionDetailsById[interaction.sessionId];
            const updateMessages = (messages: LocalMessage[]) => appendMessageIfMissing(
              withoutStreamingMessage(messages, eventStreamId),
              assistantMessage
            );
            return {
              ...collection,
              ...applyActiveSessionStreamFields({
                activeSessionId: prevState.activeSessionId,
                ...collection,
              }),
              activeSession:
                prevState.activeSession?.id === interaction.sessionId
                  ? {
                      ...prevState.activeSession,
                      messages: updateMessages(prevState.activeSession.messages),
                      messageCount: Math.max(
                        prevState.activeSession.messageCount,
                        updateMessages(prevState.activeSession.messages).length
                      ),
                    }
                  : prevState.activeSession,
              sessionDetailsById: cachedSession
                ? {
                    ...prevState.sessionDetailsById,
                    [interaction.sessionId]: {
                      ...cachedSession,
                      messages: updateMessages(cachedSession.messages),
                      messageCount: Math.max(
                        cachedSession.messageCount,
                        updateMessages(cachedSession.messages).length
                      ),
                    },
                  }
                : prevState.sessionDetailsById,
            };
          });
          break;
        }
        if (!event.responseMessage) return;
        const responseMessage = toLocalMessage(event.responseMessage);

        set((prevState) => {
          const cachedSession = prevState.sessionDetailsById[interaction.sessionId];
          const updateMessages = (messages: LocalMessage[]) =>
            insertBeforeStreamingMessage(
              messages.map((message) =>
                message.id === interaction.assistantMessageId
                  ? { ...message, interaction }
                  : message
              ),
              responseMessage,
              eventStreamId
            );

          return {
            activeSession:
              prevState.activeSession?.id === interaction.sessionId
                ? {
                    ...prevState.activeSession,
                    messages: updateMessages(prevState.activeSession.messages),
                    messageCount: Math.max(
                      prevState.activeSession.messageCount,
                      updateMessages(prevState.activeSession.messages).filter((message) => !message.isStreaming).length
                    ),
                  }
                : prevState.activeSession,
            sessionDetailsById: cachedSession
              ? {
                  ...prevState.sessionDetailsById,
                  [interaction.sessionId]: {
                    ...cachedSession,
                    messages: updateMessages(cachedSession.messages),
                    messageCount: Math.max(
                      cachedSession.messageCount,
                      updateMessages(cachedSession.messages).filter((message) => !message.isStreaming).length
                    ),
                  },
                }
              : prevState.sessionDetailsById,
          };
        });
        break;
      }

      case 'error':
        if (!eventStreamId) {
          set({ streamError: event.error || 'An error occurred' });
          break;
        }
        set((prevState) => {
          const currentStream = prevState.streamsById[eventStreamId];
          const sessionId = currentStream?.sessionId ?? prevState.streamForSession;
          const collection = removeStream(prevState, eventStreamId);
          const derivedFields = applyActiveSessionStreamFields({
            activeSessionId: prevState.activeSessionId,
            ...collection,
          });

          return {
            ...collection,
            ...derivedFields,
            streamError:
              prevState.activeSessionId === sessionId
                ? event.error || 'An error occurred'
                : derivedFields.streamError,
            activeSession:
              prevState.activeSession?.id === sessionId
                ? {
                    ...prevState.activeSession,
                    messages: withoutStreamingMessage(prevState.activeSession.messages, eventStreamId),
                  }
                : prevState.activeSession,
          };
        });
        break;

      case 'provider_connection_required':
        if (!eventStreamId) break;
        set((prevState) => {
          const currentStream = prevState.streamsById[eventStreamId];
          if (!currentStream) return prevState;
          const collection = removeStream(prevState, eventStreamId);
          const provider = event.provider === 'codex' ? 'codex' : 'claude';
          return {
            ...collection,
            ...applyActiveSessionStreamFields({
              activeSessionId: prevState.activeSessionId,
              ...collection,
            }),
            providerConnectionRequired: {
              provider,
              sessionId: currentStream.sessionId,
            },
            activeSession:
              prevState.activeSession?.id === currentStream.sessionId
                ? {
                    ...prevState.activeSession,
                    messages: withoutStreamingMessage(prevState.activeSession.messages, eventStreamId),
                  }
                : prevState.activeSession,
          };
        });
        break;

      case 'exit': {
        // Process exited - could be normal or error
        if (stream && eventStreamId && isRunningStream(stream)) {
          if (
            stream.state === 'connecting' &&
            event.code === 0 &&
            !stream.startTime &&
            !stream.text &&
            stream.content.length === 0
          ) {
            break;
          }

          // If we still have streaming text, save it to the original session
          const exitText = stream.text;
          const originalSessionId = stream.sessionId;
          const exitStreamId = stream.streamId;

          set((prevState) => {
            const collection = removeStream(prevState, exitStreamId);
            return {
              ...collection,
              ...applyActiveSessionStreamFields({
                activeSessionId: prevState.activeSessionId,
                ...collection,
              }),
              activeSession:
                prevState.activeSession?.id === originalSessionId
                  ? {
                      ...prevState.activeSession,
                      messages: withoutStreamingMessage(prevState.activeSession.messages, exitStreamId),
                    }
                  : prevState.activeSession,
            };
          });

          if (exitText) {
            get().addAssistantMessageToSession(originalSessionId, exitText, exitStreamId);
          }
        }
        break;
      }

      case 'cancelled':
        if (!eventStreamId) break;
        set((prevState) => {
          const currentStream = prevState.streamsById[eventStreamId];
          const collection = removeStream(prevState, eventStreamId);
          return {
            ...collection,
            ...applyActiveSessionStreamFields({
              activeSessionId: prevState.activeSessionId,
              ...collection,
            }),
            activeSession:
              prevState.activeSession?.id === currentStream?.sessionId
                ? {
                    ...prevState.activeSession,
                    messages: withoutStreamingMessage(prevState.activeSession.messages, eventStreamId),
                  }
                : prevState.activeSession,
          };
        });
        break;

      case 'activity':
        // Activity heartbeat - update elapsed time and active tool info
        updateStream((currentStream) => ({
          ...currentStream,
          elapsed: event.elapsed || 0,
          activeToolName: event.activeToolName || null,
          activeToolId: event.activeToolId || null,
          lastActivityTime: Date.now(),
        }));
        break;
    }
  },

  /**
   * Cancel the current streaming operation
   */
  cancelStreaming: (streamId?: string) => {
    const state = get();
    const idToCancel = streamId ?? getSessionStream(state, state.activeSessionId)?.streamId;
    if (!idToCancel) return;

    set((prevState) => {
      const stream = prevState.streamsById[idToCancel];
      if (!stream) return prevState;

      const streamsById = {
        ...prevState.streamsById,
        [idToCancel]: {
          ...stream,
          state: 'cancelling' as StreamingState,
        },
      };

      return {
        streamsById,
        ...applyActiveSessionStreamFields({
          activeSessionId: prevState.activeSessionId,
          activeStreamsBySession: prevState.activeStreamsBySession,
          streamsById,
        }),
      };
    });
    // The actual cancellation is handled by the WebSocket hook
    // This just updates the UI state
  },

  /**
   * Clear any streaming error
   */
  clearStreamError: () => {
    set({ streamError: null });
  },

  clearProviderConnectionRequired: () => {
    set({ providerConnectionRequired: null });
  },

  // ----------------------------------------
  // Composer Pre-fill Actions
  // ----------------------------------------

  /**
   * Set or clear text pending injection into the chat composer.
   * Caller (e.g. SkillsCatalog) sets this on Use; the composer reads
   * it via an effect and clears it after populating its textarea.
   */
  setPendingComposerText: (text: string | null) => {
    set({ pendingComposerText: text });
  },

  /**
   * Request text be spliced into the chat composer at the current caret.
   * The file-reference picker (ADR-0009) sets this with a backticked path; the
   * composer reads it via an effect, inserts at the cursor, and clears it.
   */
  setPendingComposerInsert: (text: string | null) => {
    set({ pendingComposerInsert: text });
  },

  /**
   * Request existing workspace image paths be attached to the chat composer.
   * The composer appends these as thumbnails and clears this queue.
   */
  setPendingComposerImages: (images: string[]) => {
    set({ pendingComposerImages: images });
  },

  /**
   * Open or close the Skills Catalog modal. Sparkles button in the
   * composer and the global Cmd/Ctrl+K shortcut both go through here.
   */
  setIsCatalogOpen: (open: boolean) => {
    set({ isCatalogOpen: open });
  },

  // ----------------------------------------
  // Utilities
  // ----------------------------------------

  /**
   * Get the active stream for a session.
   */
  getStreamForSession: (sessionId: string | null | undefined) => {
    return getSessionStream(get(), sessionId);
  },

  /**
   * Check whether a session is currently producing a response.
   */
  isSessionStreaming: (sessionId: string | null | undefined) => {
    return isRunningStream(getSessionStream(get(), sessionId));
  },

  /**
   * Get a session by ID
   */
  getSessionById: (id: string) => {
    return get().sessions.find((s) => s.id === id);
  },

  /**
   * Clear all errors
   */
  clearError: () => {
    set({
      sessionsError: null,
      sessionError: null,
      streamError: null,
      providerConnectionRequired: null,
    });
  },

  /**
   * Reset store to initial state
   */
  reset: () => {
    set(initialState);
  },
}));

// ============================================
// Selectors
// ============================================

/**
 * Select all sessions
 */
export const selectSessions = (state: ChatState) => state.sessions;

/**
 * Select sessions loading state
 */
export const selectSessionsLoading = (state: ChatState) => state.sessionsLoading;

/**
 * Select sessions error
 */
export const selectSessionsError = (state: ChatState) => state.sessionsError;

/**
 * Select active session ID
 */
export const selectActiveSessionId = (state: ChatState) => state.activeSessionId;

/**
 * Select active session with messages
 */
export const selectActiveSession = (state: ChatState) => state.activeSession;

/**
 * Select session loading state
 */
export const selectSessionLoading = (state: ChatState) => state.sessionLoading;

/**
 * Select session error
 */
export const selectSessionError = (state: ChatState) => state.sessionError;

/**
 * Select streaming state
 */
export const selectStreamingState = (state: ChatState) => state.streamingState;

/**
 * Check if currently streaming
 */
export const selectIsStreaming = (state: ChatState) =>
  state.streamingState === 'streaming' || state.streamingState === 'connecting';

/**
 * Select current streaming text
 */
export const selectStreamingText = (state: ChatState) => state.streamingText;

/**
 * Select stream error
 */
export const selectStreamError = (state: ChatState) => state.streamError;

/**
 * Select messages from active session
 */
export const selectMessages = (state: ChatState) =>
  state.activeSession?.messages || [];

/**
 * Select the Claude session ID for --resume
 */
export const selectClaudeSessionId = (state: ChatState) =>
  state.activeSession?.claudeSessionId || null;

export default useChatStore;
